import { useCallback, useMemo } from "react";

import { useChatMediaPages, type ChatMediaPage } from "~/entities/chat";

import { useAttachmentPicker } from "./useAttachmentPicker";
import type { PerKindCounts, PerKindLimits, PickerSourceItem, UseAttachmentPickerResult } from "./types";

/** Размер одной страницы ленты (потолок сервера — 500). */
const PAGE_SIZE = 100;

export interface UseChatMediaPickerParams<T extends { id: string }> {
    chatId: string | null;
    /** Per-kind лимиты режима. */
    limits: PerKindLimits;
    /** Уже в pendingByChat для каждого kind. */
    currentCounts: PerKindCounts;
    enabled: boolean;
    /** Страница ленты чата: без курсора — начало, с курсором — то, что старше. */
    loadPage: (chatId: string, cursor: string | undefined, limit: number) => Promise<ChatMediaPage<T>>;
    /** Как объект источника ложится на тайл грида. */
    toItem: (entity: T) => PickerSourceItem;
}

/**
 * Общая механика обоих пикеров: страничная лента чата + выбор в гриде.
 *
 * Различаются они только источником (загруженные вложения против
 * сгенерированных артефактов) и раскладкой объекта на тайл — всё остальное
 * (курсоры, догрузка, лимиты, выделение) одинаково, поэтому живёт здесь, а не
 * в двух почти совпадающих копиях.
 */
export function useChatMediaPicker<T extends { id: string }>({
    chatId,
    limits,
    currentCounts,
    enabled,
    loadPage,
    toItem,
}: UseChatMediaPickerParams<T>): UseAttachmentPickerResult {
    const fetchPage = useCallback(
        async (cursor: string | undefined): Promise<ChatMediaPage<T>> => {
            if (!chatId) return { items: [], total: 0 };
            return loadPage(chatId, cursor, PAGE_SIZE);
        },
        [chatId, loadPage],
    );

    const feed = useChatMediaPages<T>({
        resetKey: chatId ?? "",
        enabled: enabled && !!chatId,
        loadPage: fetchPage,
    });

    const items = useMemo<PickerSourceItem[]>(() => feed.items.map(toItem), [feed.items, toItem]);

    return useAttachmentPicker({
        chatId,
        limits,
        currentCounts,
        items,
        isLoading: feed.isLoading,
        isError: feed.isError,
        refetch: feed.refetch,
        hasMore: feed.hasMore,
        isLoadingMore: feed.isLoadingMore,
        loadMore: feed.loadMore,
    });
}
